'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { motion, AnimatePresence } from 'framer-motion';
import { api, WeightProfileResponse, WeightStructureNode } from '@/lib/api';

interface WeightViewerProps {
    profileId: string | null;
    onClose: () => void;
}

export function WeightViewer({ profileId, onClose }: WeightViewerProps) {
    const locale = useLocale();
    const t = useTranslations('weights');
    const [profile, setProfile] = useState<WeightProfileResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
    const [allNodeIds, setAllNodeIds] = useState<string[]>([]);
    const modalRef = useRef<HTMLDivElement>(null);

    // Helper to get localized name for a weight node
    const getNodeName = (node: WeightStructureNode) =>
        locale === 'pt' ? node.name_pt : node.name_en;

    // Helper to get localized profile name
    const getProfileName = () => {
        if (!profile) return '';
        return locale === 'pt' ? profile.name_pt : profile.name_en;
    };

    // Collect all node IDs
    const collectAllIds = useCallback((nodes: WeightStructureNode[]): string[] => {
        const ids: string[] = [];
        for (const node of nodes) {
            ids.push(node.id);
            if (node.children) {
                ids.push(...collectAllIds(node.children));
            }
        }
        return ids;
    }, []);

    useEffect(() => {
        if (!profileId) return;

        const fetchProfile = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await api.getWeightProfile(profileId);
                setProfile(data);

                // Expand all nodes by default
                const allIds = collectAllIds(data.weight_tree);
                setAllNodeIds(allIds);
                setExpandedNodes(new Set(allIds));
            } catch {
                setError(t('loadError'));
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, [profileId, collectAllIds, t]);

    // Click outside to close
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    // Escape key to close
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [onClose]);

    const toggleExpand = (nodeId: string) => {
        setExpandedNodes(prev => {
            const next = new Set(prev);
            if (next.has(nodeId)) {
                next.delete(nodeId);
            } else {
                next.add(nodeId);
            }
            return next;
        });
    };

    const toggleAll = () => {
        if (expandedNodes.size === allNodeIds.length) {
            // All expanded, collapse all
            setExpandedNodes(new Set());
        } else {
            // Some collapsed, expand all
            setExpandedNodes(new Set(allNodeIds));
        }
    };

    const isAllExpanded = expandedNodes.size === allNodeIds.length;

    const renderNode = (node: WeightStructureNode, parentWeight: number, depth: number = 0): React.ReactNode => {
        const hasChildren = node.children && node.children.length > 0;
        const isExpanded = expandedNodes.has(node.id);
        const effectiveGlobal = parentWeight * node.weighting;

        return (
            <div key={node.id} className={`${depth > 0 ? 'ml-4' : ''}`}>
                <div
                    className={`flex items-center justify-between p-2.5 border border-white/20 mb-1.5 transition-colors ${hasChildren ? 'cursor-pointer hover:bg-white/20' : 'bg-white/10 hover:bg-white/20'}`}
                    onClick={hasChildren ? () => toggleExpand(node.id) : undefined}
                >
                    <div className="flex items-center gap-2 flex-1">
                        {hasChildren ? (
                            <motion.div
                                className="w-6 h-6 flex items-center justify-center text-black"
                                whileTap={{ scale: 0.9 }}
                            >
                                <motion.svg
                                    className="w-4 h-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    animate={{ rotate: isExpanded ? 90 : 0 }}
                                    transition={{ duration: 0.15 }}
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </motion.svg>
                            </motion.div>
                        ) : (
                            <div className="w-6" />
                        )}
                        <span className={`text-sm ${node.level === 'cluster' ? 'font-bold text-black' : 'text-slate-700'}`}>
                            {getNodeName(node)}
                        </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                        <span className="text-slate-500 font-medium">
                            {(node.weighting * 100).toFixed(1)}%
                        </span>
                        <span className="text-black font-bold bg-white/30 px-2 py-0.5 border border-white/20">
                            {(effectiveGlobal * 100).toFixed(2)}%
                        </span>
                    </div>
                </div>

                <AnimatePresence>
                    {hasChildren && isExpanded && (
                        <motion.div
                            className="mb-1"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            {node.children.map((child: WeightStructureNode) =>
                                renderNode(child, effectiveGlobal, depth + 1)
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        );
    };

    if (!profileId) return null;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
                ref={modalRef}
                className="w-full max-w-8xl max-h-[80vh] flex flex-col bg-white/80 backdrop-blur-md shadow-2xl border border-white/20 overflow-hidden rounded-none"
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.2 }}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-white/20 bg-transparent">
                    <div>
                        <h2 className="text-xl font-bold text-black">
                            {loading ? t('loading') : getProfileName()}
                        </h2>
                        <p className="text-sm text-slate-500">{t('weightDistribution')}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* Expand/Collapse Toggle */}
                        {!loading && profile && (
                            <button
                                onClick={toggleAll}
                                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-black hover:bg-white/20 transition-colors cursor-pointer"
                            >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    {isAllExpanded ? (
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    ) : (
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    )}
                                </svg>
                                {isAllExpanded ? t('collapseAll') : t('expandAll')}
                            </button>
                        )}
                        <button
                            onClick={onClose}
                            className="p-2 text-slate-400 hover:text-black hover:bg-white/20 transition-colors cursor-pointer"
                        >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-5 scrollbar-thin scrollbar-thumb-gray-300">
                    {loading && (
                        <div className="text-center text-slate-500 py-8">{t('loadingWeights')}</div>
                    )}
                    {error && (
                        <div className="text-center text-red-600 py-8">{error}</div>
                    )}
                    {!loading && !error && profile && (
                        <div className="space-y-1">
                            {profile.weight_tree.map((node: WeightStructureNode) =>
                                renderNode(node, 1.0)
                            )}
                        </div>
                    )}
                </div>
            </motion.div>
        </div>
    );
}
